import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../utils/AxiosHelper';
import { useAppSelector } from '../hooks';
import API from '../../modules/settings/APIs';

export interface StatusRecord {
  id?: string | number;
  code: string;
  name: string;
  description?: string;
  entity: string;
  sequence?: number;
  active?: boolean;
}

export const statusKeys = {
  all: ['status-rodeesin'] as const,
  list: (entity?: string) => ['status-rodeesin', 'list', entity] as const,
  entities: ['status-rodeesin', 'entities'] as const,
} as const;

const unwrap = (response: any) => response.data?.data ?? response.data;

export const useStatusList = (entity?: string) => {
  const { isAuthenticated, token } = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: statusKeys.list(entity),
    queryFn: async () => {
      const response = await apiRequest.get(API.status.list, {
        params: entity ? { entity } : undefined,
      });
      return unwrap(response) as StatusRecord[];
    },
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated && !!token,
  });
};

export const useStatusEntities = () => {
  const { isAuthenticated, token } = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: statusKeys.entities,
    queryFn: async () => {
      const response = await apiRequest.get(API.status.entities);
      return unwrap(response) as string[];
    },
    staleTime: 10 * 60 * 1000,
    enabled: isAuthenticated && !!token,
  });
};

export const useCreateStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StatusRecord) => {
      const response = await apiRequest.post(API.status.create, data);
      return unwrap(response) as StatusRecord;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statusKeys.all });
    },
  });
};

export const useUpdateStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: StatusRecord }) => {
      const response = await apiRequest.put(API.status.update(id), data);
      return unwrap(response) as StatusRecord;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statusKeys.all });
    },
  });
};

export const useDeleteStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string | number) => {
      const response = await apiRequest.delete(API.status.delete(id));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statusKeys.all });
    },
  });
};
